import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, ChevronRight, PenLine, WifiOff } from 'lucide-react';
import type { CurrentHandover, Handover, HandoverItem, MyHandoverLine } from '@up2d8/contracts';
import { AppSubBar, Badge, Button, EmptyState, cn, useToast } from '@up2d8/ui';
import { problemDetail } from '@up2d8/api-client';
import { useOffline } from '../../../offline/use-offline';
import { useSession } from '../../../shared/session';
import { isAwaitingData } from '../../../shared/query-state';
import { renderAppTabLink, useAppTabs } from '../app-tabs';
import { DeskShell } from '../desk/desk-shell';
import { MessagesIcon } from '../messages-icon';
import { NotificationBell } from '../notification-bell';
import { CloseItemDialog, HandoverCard, HandoverIcon, PhotoGrid, StatusBadge } from './handover-ui';
import {
  clock,
  shortDate,
  useCurrentHandover,
  useHandover,
  useHandoverActions,
  useHandoverHistory,
  useHandoverLines,
  useHandoverReaders,
  weekdayShort,
} from './use-handover';

/**
 * De ploegoverdracht lezen (Stap 39, OVD-2, spec §3 frame 2 en §4 "Lezen").
 *
 * Drie ingangen: `/app/overdracht` (de eerste of enige lijn), `/app/overdracht/lijn/$lineId` (na
 * de keuzechips) en `/app/overdracht/$handoverId` (uit de strook, de melding of de historiek).
 *
 * Eén DOM voor telefoon en bureau, zoals de rest van de app: twee kolommen vanaf 1100 px, met
 * "Gelezen", notities en foto's rechts. Op de telefoon staat "Gelezen" als vaste voet onderaan.
 */
export function AppHandoverPage({
  lineId = null,
  handoverId = null,
}: {
  lineId?: string | null;
  handoverId?: string | null;
}) {
  const { t } = useTranslation();
  const tabs = useAppTabs();
  const navigate = useNavigate();
  const lines = useHandoverLines();

  const gekozen = lineId ?? (handoverId ? null : (lines.data?.[0]?.lineId ?? null));
  const current = useCurrentHandover(handoverId ? null : gekozen);
  const one = useHandover(handoverId);

  const result = handoverId ? one : current;
  const handover: Handover | null = handoverId
    ? (one.data?.data ?? null)
    : (current.data?.data?.handover ?? null);
  const effectiveLineId = handover?.lineId ?? gekozen;
  const line = (lines.data ?? []).find((candidate) => candidate.lineId === effectiveLineId) ?? null;

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="handover"
      renderLink={renderAppTabLink}
    >
      <AppSubBar title={t('handover.title')} onBack={() => void navigate({ to: '/app' })} />

      {!handoverId ? <LineChips lines={lines.data ?? []} lineId={gekozen} /> : null}

      {result.data?.fromCache ? (
        <div
          className="flex items-start gap-2 rounded-md bg-border-light p-3.5 text-small text-text-secondary"
          data-testid="handover-offline"
        >
          <WifiOff aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
          {t('handover.offlineRead')}
        </div>
      ) : null}

      {/* Zonder lijn haalt `current` niets op; `isPending` alleen bleef dan eeuwig laden (#14). */}
      {isAwaitingData(lines) || isAwaitingData(result) ? (
        <MachineSkeleton />
      ) : (lines.data ?? []).length === 0 && !handoverId ? (
        <HandoverCard data-testid="handover-no-lines">
          <EmptyState title={t('handover.noLinesTitle')} description={t('handover.noLinesHint')} />
        </HandoverCard>
      ) : handover ? (
        <HandoverReadView
          handover={handover}
          line={line}
          missing={handoverId ? null : (current.data?.data ?? null)}
        />
      ) : current.data?.data?.missing ? (
        <MissingView current={current.data.data} line={line} />
      ) : (
        <FirstHandover line={line} />
      )}
    </DeskShell>
  );
}

/** Spec §5: "Lid van meerdere lijnen" - eerst de chips, zoals de teamkeuze in Mijn team. */
function LineChips({ lines, lineId }: { lines: MyHandoverLine[]; lineId: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (lines.length <= 1) return null;
  return (
    <div className="flex flex-col gap-2">
      <span className="font-heading text-small font-semibold text-text-secondary">
        {t('handover.whichLine')}
      </span>
      <div className="flex flex-wrap gap-2" data-testid="handover-line-chips">
        {lines.map((line) => (
          <Button
            key={line.lineId}
            size="sm"
            variant={line.lineId === lineId ? 'primary' : 'secondary'}
            onClick={() =>
              void navigate({
                to: '/app/overdracht/lijn/$lineId',
                params: { lineId: line.lineId },
              })
            }
          >
            {line.lineName}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Spec §5 "Laden": skeleton-kaarten in de vorm van de machinekaart. */
function MachineSkeleton() {
  return (
    <div className="flex flex-col gap-2.5" aria-busy="true" data-testid="handover-loading">
      {[0, 1, 2].map((index) => (
        <HandoverCard key={index} compact>
          <div className="flex items-center justify-between">
            <span className="h-4 w-32 animate-pulse rounded-pill bg-border-light" />
            <span className="h-6 w-20 animate-pulse rounded-pill bg-border-light" />
          </div>
          <span className="mt-2 block h-3.5 w-48 animate-pulse rounded-pill bg-border-light" />
        </HandoverCard>
      ))}
    </div>
  );
}

/** Spec §5 "Eerste overdracht voor deze lijn". */
function FirstHandover({ line }: { line: MyHandoverLine | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const slotId = line?.currentSlotId ?? line?.slots[0]?.id ?? null;
  return (
    <HandoverCard data-testid="handover-first">
      <EmptyState
        icon={<HandoverIcon size={32} />}
        title={t('handover.firstTitle')}
        description={t('handover.firstHint')}
        action={
          line && slotId ? (
            <Button
              data-testid="handover-first-write"
              onClick={() =>
                void navigate({
                  to: '/app/overdracht/schrijven/$lineId/$slotId',
                  params: { lineId: line.lineId, slotId },
                })
              }
            >
              {t('handover.firstWrite')}
            </Button>
          ) : undefined
        }
      />
    </HandoverCard>
  );
}

/** Spec §5 "Vorige ploeg diende niets in": de open punten van de laatste overdracht die er was. */
function MissingView({ current, line }: { current: CurrentHandover; line: MyHandoverLine | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-element" data-testid="handover-missing">
      <HandoverCard className="border-2 border-[var(--brand-orange-aa)] bg-brand-orange-tint">
        <p className="font-heading text-title-sm font-semibold text-text-primary">
          {t('handover.missingTitle', { slot: current.missing?.slotName ?? '' })}
        </p>
      </HandoverCard>
      <OpenItemsSection items={current.openItems} canClose={false} />
      <WriteButton line={line} />
    </div>
  );
}

function WriteButton({ line }: { line: MyHandoverLine | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { has } = useSession();
  const slotId = line?.currentSlotId ?? null;
  if (!line || !slotId || !has('shift_handover.handover.write')) return null;
  const slot = line.slots.find((candidate) => candidate.id === slotId);
  return (
    <Button
      variant="secondary"
      data-testid="handover-write"
      onClick={() =>
        void navigate({
          to: '/app/overdracht/schrijven/$lineId/$slotId',
          params: { lineId: line.lineId, slotId },
        })
      }
    >
      <PenLine aria-hidden className="mr-2 h-4 w-4" />
      {t('handover.writeFor', { slot: slot?.name ?? '' })}
    </Button>
  );
}

function HandoverReadView({
  handover,
  line,
}: {
  handover: Handover;
  line: MyHandoverLine | null;
  missing: CurrentHandover | null;
}) {
  const { t, i18n } = useTranslation();
  const { has } = useSession();
  const { online } = useOffline();
  const toast = useToast();
  const { read } = useHandoverActions();
  const [uitgeklapt, setUitgeklapt] = useState(false);

  const stil = handover.assets.filter((asset) => asset.status === 'stopped');
  const storing = handover.assets.filter((asset) => asset.status === 'fault');
  const draaien = handover.assets.filter((asset) => asset.status === 'running');
  const open = handover.items.filter((item) => item.status === 'open');

  const submitted = handover.status !== 'draft';
  // Wie de overdracht schreef, leest ze niet "voor": geen knop, wel de stand van wie las.
  const canRead = submitted && !handover.isMine;
  const meta = t('handover.submittedMeta', {
    date: shortDate(handover.shiftDate, i18n.language),
    time: handover.submittedAt ? clock(handover.submittedAt, i18n.language) : '',
    name: handover.authorName,
  });

  const gelezenKnop = handover.myReadAt ? (
    <p
      className="flex items-center justify-center gap-2 font-heading text-content font-semibold text-success-tint-text"
      data-testid="handover-read-at"
    >
      <CheckCircle2 aria-hidden className="h-5 w-5" />
      {t('handover.readAt', { time: clock(handover.myReadAt, i18n.language) })}
    </p>
  ) : canRead ? (
    <Button
      size="lg"
      className="w-full"
      disabled={!online}
      loading={read.isPending}
      data-testid="handover-mark-read"
      onClick={() =>
        read.mutate(handover.id, {
          onError: (error) =>
            toast.show({
              tone: 'danger',
              title: t('errors.generic'),
              description: problemDetail(error) ?? undefined,
            }),
        })
      }
    >
      <CheckCircle2 aria-hidden className="mr-2 h-5 w-5" />
      {t('handover.markRead')}
    </Button>
  ) : null;

  return (
    <div
      className="flex flex-col gap-element pb-24 wide:grid wide:grid-cols-[minmax(0,1fr)_320px] wide:items-start wide:gap-5 wide:pb-0"
      data-testid="handover-read"
    >
      <div className="flex min-w-0 flex-col gap-element">
        {/* Kop: lijn en dienst, wie diende in en wanneer; op het bureau met de badges rechts. */}
        <HandoverCard>
          <div className="flex items-start gap-3">
            <span
              aria-hidden
              className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-md bg-brand-orange-tint text-[var(--orange-tint-text-aa)] desk:flex"
            >
              <HandoverIcon size={22} />
            </span>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-[22px] font-semibold text-text-primary desk:text-title-md">
                {handover.lineName} · {handover.slotName}
              </h1>
              <p className="text-small text-text-secondary">{meta}</p>
            </div>
            <div className="hidden flex-wrap justify-end gap-1.5 desk:flex">
              {handover.status === 'locked' ? (
                <Badge tone="neutral" data-testid="handover-locked">
                  {t('handover.locked')}
                </Badge>
              ) : null}
              {stil.length > 0 ? (
                <StatusBadge status="stopped">
                  {t('handover.countStopped', { count: stil.length })}
                </StatusBadge>
              ) : null}
              {storing.length > 0 ? (
                <StatusBadge status="fault">
                  {t('handover.countFault', { count: storing.length })}
                </StatusBadge>
              ) : null}
              <Badge tone="orange">{t('handover.countOpen', { count: open.length })}</Badge>
            </div>
          </div>
          {handover.status === 'locked' ? (
            <div className="mt-2 desk:hidden">
              <Badge tone="neutral">{t('handover.locked')}</Badge>
            </div>
          ) : null}
        </HandoverCard>

        {/* Spec §5: zonder machines ontbreekt het blok volledig. */}
        {handover.assets.length > 0 ? (
          <section className="flex flex-col gap-2.5" data-testid="handover-machines">
            <h2 className="flex items-center gap-2 font-heading text-[17px] font-semibold text-text-primary">
              {t('handover.machines')}
              <Badge tone="neutral">{handover.assets.length}</Badge>
            </h2>
            {[...stil, ...storing].map((asset) => (
              <HandoverCard key={asset.assetId} data-testid="handover-machine">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-heading text-content font-semibold text-text-primary">
                    {asset.name}
                  </span>
                  <StatusBadge status={asset.status} />
                </div>
                {asset.note ? (
                  <p className="text-[15px] text-text-secondary">{asset.note}</p>
                ) : null}
              </HandoverCard>
            ))}
            {draaien.length > 0 ? (
              <HandoverCard>
                <button
                  type="button"
                  className="flex min-h-12 w-full items-center gap-3 text-left"
                  aria-expanded={uitgeklapt}
                  onClick={() => setUitgeklapt((value) => !value)}
                  data-testid="handover-running-toggle"
                >
                  <StatusBadge status="running" />
                  <span className="flex-1 text-content font-medium text-text-primary">
                    {t('handover.runningCount', { count: draaien.length })}
                  </span>
                  <ChevronDown
                    aria-hidden
                    className={cn(
                      'h-5 w-5 text-text-muted transition-transform',
                      uitgeklapt && 'rotate-180',
                    )}
                  />
                </button>
                {uitgeklapt ? (
                  <ul className="mt-2 flex flex-col">
                    {draaien.map((asset) => (
                      <li
                        key={asset.assetId}
                        className="border-t border-border-light py-2 text-content text-text-primary"
                      >
                        {asset.name}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </HandoverCard>
            ) : null}
          </section>
        ) : null}

        <OpenItemsSection
          items={handover.items.filter((item) => item.status === 'open')}
          canClose={has('shift_handover.handover.write') && handover.status !== 'draft' && online}
        />

        <WriteButton line={line} />
        <HistorySection lineId={handover.lineId} currentId={handover.id} />
      </div>

      <div className="flex flex-col gap-element">
        {/* Telefoon: vaste voet boven de onderbalk. Bureau: de eerste kaart rechts. */}
        <div className="order-last wide:order-first">
          <div className="fixed inset-x-0 bottom-16 z-10 border-t border-border bg-surface p-3 desk:static desk:border-0 desk:bg-transparent desk:p-0">
            <div className="mx-auto max-w-md desk:max-w-none">
              <HandoverCard className="desk:flex desk:flex-col desk:gap-3 max-desk:border-0 max-desk:p-0 max-desk:shadow-none">
                {gelezenKnop}
                {submitted ? <ReadersLine handover={handover} /> : null}
              </HandoverCard>
            </div>
          </div>
        </div>

        {handover.notes ? (
          <HandoverCard data-testid="handover-notes">
            <h2 className="font-heading text-[17px] font-semibold text-text-primary">
              {t('handover.notes')}
            </h2>
            <p className="whitespace-pre-line text-content text-text-primary">{handover.notes}</p>
          </HandoverCard>
        ) : null}

        {handover.fileIds.length > 0 ? (
          <HandoverCard>
            <h2 className="flex items-center gap-2 font-heading text-[17px] font-semibold text-text-primary">
              {t('handover.photos')}
              <Badge tone="neutral">{handover.fileIds.length}</Badge>
            </h2>
            <PhotoGrid fileIds={handover.fileIds} />
          </HandoverCard>
        ) : null}

        <Link
          to="/app/melden"
          className="flex min-h-12 items-center gap-2 font-heading text-content font-semibold text-[var(--brand-primary-aa)]"
          data-testid="handover-report"
        >
          {t('handover.alsoReport')}
          <ChevronRight aria-hidden className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

/**
 * "Gelezen door 6 van 8" voor de teamleider van de ploeg, "6 van 8 collega's lazen deze
 * overdracht" voor de rest (spec §1). Welke van de twee, beslist de server.
 */
function ReadersLine({ handover }: { handover: Handover }) {
  const { t, i18n } = useTranslation();
  const { online } = useOffline();
  const readers = useHandoverReaders(handover.id, online);
  const [toon, setToon] = useState(false);
  if (!readers.data || readers.data.expected === 0) return null;

  const { expected, read, people } = readers.data;
  if (people === null) {
    return (
      <p
        className="mt-2 text-center text-small text-text-secondary desk:mt-0"
        data-testid="handover-readers"
      >
        {t('handover.readersColleagues', { read, expected })}
      </p>
    );
  }

  return (
    <div className="mt-2 desk:mt-0" data-testid="handover-readers">
      <button
        type="button"
        className="flex w-full items-center justify-center gap-1 text-small font-semibold text-text-secondary"
        aria-expanded={toon}
        onClick={() => setToon((value) => !value)}
      >
        {t('handover.readersLeader', { read, expected })}
        <ChevronDown aria-hidden className={cn('h-4 w-4', toon && 'rotate-180')} />
      </button>
      {toon ? (
        <ul
          className="mt-2 flex max-h-60 flex-col overflow-y-auto"
          data-testid="handover-readers-list"
        >
          {people.map((person) => (
            <li
              key={person.personId}
              className="flex items-center justify-between border-t border-border-light py-2 text-small"
            >
              <span className="text-text-primary">{person.name}</span>
              <span className={person.readAt ? 'text-success-tint-text' : 'text-text-muted'}>
                {person.readAt ? clock(person.readAt, i18n.language) : t('handover.notReadYet')}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function OpenItemsSection({ items, canClose }: { items: HandoverItem[]; canClose: boolean }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { closeItem } = useHandoverActions();
  const [sluit, setSluit] = useState<HandoverItem | null>(null);

  return (
    <section className="flex flex-col gap-2.5" data-testid="handover-items">
      <h2 className="flex items-center gap-2 font-heading text-[17px] font-semibold text-text-primary">
        {t('handover.openItems')}
        <Badge tone="neutral">{items.length}</Badge>
      </h2>
      {items.length === 0 ? (
        <p className="text-content text-text-secondary">{t('handover.noOpenItems')}</p>
      ) : (
        items.map((item) => (
          <HandoverCard key={item.id} data-testid="handover-item">
            <p className="font-heading text-content font-semibold text-text-primary">{item.text}</p>
            <div className="flex flex-wrap items-center gap-2">
              {item.carried ? (
                <Badge tone="orange">
                  {t('handover.carriedSince', { day: weekdayShort(item.openedAt, i18n.language) })}
                </Badge>
              ) : (
                <Badge tone="primary">{t('handover.new')}</Badge>
              )}
              <span className="text-small text-text-secondary">
                {t('handover.openedBy', { name: item.openedByName })}
              </span>
            </div>
            {canClose ? (
              <Button
                variant="secondary"
                className="w-full desk:w-auto desk:self-start"
                data-testid="handover-item-close"
                onClick={() => setSluit(item)}
              >
                {t('handover.close')}
              </Button>
            ) : null}
          </HandoverCard>
        ))
      )}

      {sluit ? (
        <CloseItemDialog
          text={sluit.text}
          busy={closeItem.isPending}
          onClose={() => setSluit(null)}
          onConfirm={(note) =>
            closeItem.mutate(
              { itemId: sluit.id, note },
              {
                onSuccess: () => {
                  setSluit(null);
                  toast.show({ tone: 'success', title: t('handover.closed') });
                },
                onError: (error) =>
                  toast.show({
                    tone: 'danger',
                    title: t('errors.generic'),
                    description: problemDetail(error) ?? undefined,
                  }),
              },
            )
          }
        />
      ) : null}
    </section>
  );
}

/** De historiek per lijn (niet getekend; met de bestaande lijstkaart, `gaps.md`). */
function HistorySection({ lineId, currentId }: { lineId: string; currentId: string }) {
  const { t, i18n } = useTranslation();
  const { online } = useOffline();
  const history = useHandoverHistory(lineId, online);
  const vorige = (history.data ?? []).filter((item) => item.id !== currentId);
  if (vorige.length === 0) return null;

  return (
    <section className="flex flex-col gap-2.5" data-testid="handover-history">
      <h2 className="font-heading text-[17px] font-semibold text-text-primary">
        {t('handover.history')}
      </h2>
      <HandoverCard className="p-0">
        <ul className="flex flex-col">
          {vorige.slice(0, 10).map((item) => (
            <li key={item.id} className="border-t border-border-light first:border-t-0">
              <Link
                to="/app/overdracht/$handoverId"
                params={{ handoverId: item.id }}
                className="flex min-h-16 items-center gap-3 px-4 py-2"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-heading text-content font-semibold text-text-primary">
                    {shortDate(item.shiftDate, i18n.language)} · {item.slotName}
                  </span>
                  <span className="block text-small text-text-secondary">
                    {t('handover.historyMeta', {
                      name: item.authorName,
                      open: item.openItems,
                      read: item.readCount,
                    })}
                  </span>
                </span>
                {item.stoppedAssets > 0 ? (
                  <StatusBadge status="stopped">{item.stoppedAssets}</StatusBadge>
                ) : null}
                <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-text-muted" />
              </Link>
            </li>
          ))}
        </ul>
      </HandoverCard>
    </section>
  );
}

/** `/app/overdracht/lijn/$lineId`. */
export function AppHandoverLinePage() {
  const { lineId } = useParams({ strict: false }) as { lineId: string };
  return <AppHandoverPage lineId={lineId} />;
}

/** `/app/overdracht/$handoverId`. */
export function AppHandoverDetailPage() {
  const { handoverId } = useParams({ strict: false }) as { handoverId: string };
  return <AppHandoverPage handoverId={handoverId} />;
}
